import { FC, useEffect, useMemo, useState } from 'react';
import { fetchOrder, type OrderResponse } from '../../../api/orders';

interface Props {
  orderId: string | null;
}

const OrderGatePass: FC<Props> = ({ orderId }) => {
  const [order, setOrder] = useState<OrderResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orderId) return;
    let active = true;
    setLoading(true);
    fetchOrder(orderId)
      .then((resp) => { if (active) setOrder(resp); })
      .catch((err) => { if (active) setError(err instanceof Error ? err.message : 'Failed to load order.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [orderId]);

  const latestPaymentAt = useMemo(() => {
    if (!order?.paymentSummaries || !order.paymentSummaries.length) {
      return null;
    }
    const timestamps = order.paymentSummaries
      .map((p) => p.happenedAt || p.createdAt)
      .filter((v): v is string => Boolean(v));
    if (!timestamps.length) {
      return null;
    }
    const latest = timestamps.reduce((max, current) =>
      new Date(current).getTime() > new Date(max).getTime() ? current : max,
    timestamps[0]);
    return new Date(latest);
  }, [order]);

  const vehicleSummary = useMemo(() => {
    const v = order?.customerVehicle;
    if (!v) return null;
    const brand = v.vehicleBrand?.name ?? '';
    const model = v.vehicleModel?.name ?? '';
    const reg = v.vehicleRegistrationNumber || '';
    const parts = [brand, model].filter(Boolean).join(' ');
    if (!parts && !reg) return null;
    return `${parts || 'Vehicle'}${reg ? ` • ${reg}` : ''}`;
  }, [order]);

  const payments = order?.paymentSummaries ?? [];

  const formatAmount = (value: string | number | null | undefined) => {
    const numeric = typeof value === 'number' ? value : Number(value ?? 0);
    if (Number.isNaN(numeric)) return '0.00';
    return numeric.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  return (
    <div className="space-y-4">
      {order?.status !== 'delivered' ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <p className="font-semibold">
            After completing the payment, the gate pass will be issued automatically for this order.
          </p>
          {vehicleSummary ? (
            <p className="mt-1 text-xs text-amber-900/80">
              Vehicle: {vehicleSummary}
            </p>
          ) : null}
        </div>
      ) : (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          <p className="font-semibold">
            Payment completed and gate pass was issued for this order
            {latestPaymentAt
              ? ` at ${latestPaymentAt.toLocaleString()}`
              : '.'}
          </p>
          {vehicleSummary ? (
            <p className="mt-1 text-xs text-emerald-900/80">
              Vehicle: {vehicleSummary}
            </p>
          ) : null}
        </div>
      )}
      {error ? (
        <div className="rounded-lg border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">{error}</div>
      ) : null}
      {order ? (
        <div className="space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 text-sm text-slate-600">
            <h3 className="text-base font-semibold text-slate-700">Amount summary</h3>
            <dl className="mt-3 space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Subtotal</dt>
                <dd className="text-slate-700">{formatAmount(order.subtotalAmount ?? 0)}</dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Items discount</dt>
                <dd className="text-slate-700">{formatAmount(order.discountTotal ?? 0)}</dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Tax</dt>
                <dd className="text-slate-700">{formatAmount(order.taxTotal ?? 0)}</dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Grand total</dt>
                <dd className="text-base font-semibold text-slate-800">
                  {formatAmount(order.grandTotal ?? 0)}
                </dd>
              </div>
            </dl>
          </div>

          {order.status === 'delivered' ? (
            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <h3 className="text-base font-semibold text-slate-700">Payments</h3>
              {payments.length === 0 ? (
                <p className="mt-2 text-sm text-slate-500">No payment records found.</p>
              ) : (
                <ul className="mt-3 divide-y divide-slate-200 rounded-xl border border-slate-200 bg-white">
                  {payments.map((p) => (
                    <li key={p.id} className="flex items-center justify-between px-3 py-2">
                      <div>
                        <div className="text-slate-700">{p.paymentTypeName}</div>
                        <div className="text-xs text-slate-400">
                          {p.happenedAt ? new Date(p.happenedAt).toLocaleString() : '—'}
                        </div>
                      </div>
                      <div className="text-sm font-semibold text-slate-800">
                        {formatAmount(p.amount)}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default OrderGatePass;
